// File: src/features/long-press/LongPressContext.tsx
import React, { createContext, useState, useContext, ReactNode, useCallback, useRef } from 'react';
import LongPressIndicator from './LongPressIndicator';

interface LongPressContextType {
  showIndicator: (duration: number) => void;
  hideIndicator: () => void;
}

export const LongPressContext = createContext<LongPressContextType | undefined>(undefined);

export const useLongPressIndicator = (): LongPressContextType => {
  const context = useContext(LongPressContext);
  if (!context) {
    throw new Error('useLongPressIndicator must be used within a LongPressProvider');
  }
  return context;
};

export const LongPressProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const animationFrameRef = useRef<number | null>(null);

  const showIndicator = useCallback((duration: number) => {
    setIsVisible(true);
    setProgress(0);
    let startTime: number | null = null;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsedTime = timestamp - startTime;
      const progressValue = Math.min(elapsedTime / duration, 1);
      setProgress(progressValue);

      if (elapsedTime < duration) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);
  }, []);

  const hideIndicator = useCallback(() => {
    setIsVisible(false);
    setProgress(0);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  }, []);

  return (
    <LongPressContext.Provider value={{ showIndicator, hideIndicator }}>
      {children}
      <LongPressIndicator isVisible={isVisible} progress={progress} />
    </LongPressContext.Provider>
  );
};